import { DestroyRef, inject, Injectable } from '@angular/core';
import { StateModel } from '../models';
import { ClientService } from './client.service';
import { StateService } from './state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';
import { countriesArray } from '../helpers';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class CacheService {
	private entityStatesSubject: BehaviorSubject<StateModel[]> = new BehaviorSubject<StateModel[]>([]);
	private allStatesSubject: BehaviorSubject<StateModel[]> = new BehaviorSubject<StateModel[]>([]);

	public entityStates$: Observable<StateModel[]> = this.entityStatesSubject.asObservable();
	public allStates$: Observable<StateModel[]> = this.allStatesSubject.asObservable();

	private allStates: StateModel[] = [];
	private entityOperatingStates: StateModel[] = [];
	private clientCount: number;
	private destroyRef = inject(DestroyRef);

	constructor(
		private stateService: StateService,
		private clientService: ClientService,
	) {
	}

	public getEntityStates(): StateModel[] {
		return this.entityStatesSubject.getValue();
	}

	public getAllStates(): StateModel[] {
		return this.allStatesSubject.getValue();
	}

	public getStateById(id: string): StateModel {
		return this.allStatesSubject.getValue().find((state: StateModel) => state.id === id);
	}

	public cacheEntityStates(): void {
		this.stateService.getStates().pipe(
			takeUntilDestroyed(this.destroyRef),
			tap((states: StateModel[]) => {
				this.entityOperatingStates = states.map(state => new StateModel(state));
				this.entityStatesSubject.next(this.entityOperatingStates);
			})
		).subscribe();
	}

	public cacheAllStates() {
		const requests = countriesArray.map(country =>
			this.stateService.getAllStates({ isActive: true, country}));

		forkJoin(requests).pipe(
			takeUntilDestroyed(this.destroyRef),
			tap((results: StateModel[][]) => {
				this.allStates = results.flat().map(state => new StateModel(state));
				this.allStatesSubject.next(this.allStates);
				this.cacheEntityStates();
			})
		).subscribe();
	}

	// clients/companies
	public getClientCount(searchParams: any): number {
		this.clientService.postCount(searchParams).subscribe((count: number) => {
			this.clientCount = count;
		});

		return this.clientCount;
	}
}
